import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, CircularProgress, Container } from "@mui/material";
import { getWorkflow, WorkflowDetail } from "../api/workflows";
import { getMe, MeResponse } from "../api/me";
import { isLiveEnv, listSearchEnvs, SearchEnv } from "../api/searchEnvs";
import { extractErrorMessage } from "../api/client";
import { usePolling } from "../hooks/usePolling";
import { WorkflowDiagram } from "../components/WorkflowDiagram";
import { CollapsibleSection } from "../components/CollapsibleSection";
import { ConcurrencyEditor } from "../components/exportDetail/ConcurrencyEditor";
import { CostSummaryPanel } from "../components/exportDetail/CostSummaryPanel";
import { DataRetentionPanel } from "../components/exportDetail/DataRetentionPanel";
import { DownloadLinksPanel } from "../components/exportDetail/DownloadLinksPanel";
import { JobConfigurationPanel } from "../components/exportDetail/JobConfigurationPanel";
import { JobsTable } from "../components/exportDetail/JobsTable";
import { ManifestPanel } from "../components/exportDetail/ManifestPanel";
import { ResultsPanel } from "../components/exportDetail/ResultsPanel";
import { SearchEnvPanel } from "../components/exportDetail/SearchEnvPanel";
import { WorkflowHeader } from "../components/exportDetail/WorkflowHeader";

const TERMINAL_STATUSES = new Set(["succeeded", "failed", "cancelled"]);

// Workflow detail page. Polls /api/v1/operator/workflows/<id> every
// 5s while non-terminal and groups the per-feature components into
// three collapsible sections (Job Activity / Result Data / Customer
// Search). Auto-collapses Job Activity once the workflow terminates,
// and auto-collapses Result Data once a non-terminal search env is up
// — at that point the user is doing analysis, not reviewing the run.
export function ExportDetail() {
  const { workflowId } = useParams<{ workflowId: string }>();
  const [wf, setWf] = useState<WorkflowDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [me, setMe] = useState<MeResponse | null>(null);
  // Parallel light-weight poll for the workflow's search envs so the
  // page-level collapse logic can react to env state without coupling
  // to SearchEnvPanel's internals. 10s cadence matches the panel's.
  const [envs, setEnvs] = useState<SearchEnv[] | null>(null);

  useEffect(() => {
    getMe()
      .then(setMe)
      .catch(() => undefined);
  }, []);

  usePolling({
    fetcher: () => getWorkflow(workflowId!),
    onData: setWf,
    shouldStop: (r) => TERMINAL_STATUSES.has(r.status),
    onError: (err) => setError(extractErrorMessage(err)),
    deps: [workflowId],
  });

  usePolling({
    fetcher: () => listSearchEnvs({ workflow_id: workflowId! }),
    onData: (r) => setEnvs(r.search_envs),
    onError: () => undefined,
    intervalMs: 10_000,
    deps: [workflowId],
  });

  if (error)
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  if (!wf)
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <CircularProgress size={20} />
      </Container>
    );

  const succeeded = wf.status === "succeeded";
  const terminal = TERMINAL_STATUSES.has(wf.status);
  const hasActiveEnv = envs?.some(isLiveEnv) ?? false;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <WorkflowHeader
        wf={wf}
        me={me}
        onCancelled={setWf}
        onError={setError}
      />

      <CollapsibleSection
        title="Job Configuration"
        subtitle="inputs the workflow was submitted with"
        defaultOpen={false}
      >
        <JobConfigurationPanel wf={wf} />
      </CollapsibleSection>

      <CollapsibleSection
        title="Job Activity"
        subtitle={
          terminal
            ? `final status: ${wf.status}`
            : "in flight — concurrency, diagram, per-job table"
        }
        forceOpen={!terminal}
      >
        <ConcurrencyEditor wf={wf} me={me} onSaved={setWf} />
        <WorkflowDiagram wf={wf} />
        <JobsTable wf={wf} me={me} />
      </CollapsibleSection>

      <CollapsibleSection
        title="Result Data"
        subtitle={
          hasActiveEnv
            ? "search environment active — see Customer Search below"
            : "counters, manifest, downloads"
        }
        forceOpen={!hasActiveEnv}
      >
        <ResultsPanel wf={wf} me={me} />
        <ManifestPanel wf={wf} />
        {succeeded && workflowId && <DownloadLinksPanel workflowId={workflowId} />}
      </CollapsibleSection>

      <CollapsibleSection title="Customer Search">
        <SearchEnvPanel wf={wf} />
      </CollapsibleSection>

      <CollapsibleSection
        title="Data Retention"
        subtitle={
          hasActiveEnv
            ? "dataset + search-env TTLs (extending the env extends the dataset)"
            : "dataset TTL — extend or force-delete"
        }
      >
        <DataRetentionPanel
          wf={wf}
          onWorkflowUpdated={setWf}
          envs={envs}
          onEnvExtended={() =>
            listSearchEnvs({ workflow_id: workflowId! })
              .then((r) => setEnvs(r.search_envs))
              .catch(() => undefined)
          }
        />
      </CollapsibleSection>

      {/* Renders its own "Cost Summary" section; hidden for non-admins
          unless the dataset's projected total exceeds $200. */}
      <CostSummaryPanel wf={wf} me={me} />
    </Container>
  );
}
